import React, { useState } from "react";
import PropTypes from "prop-types";
import { productUrl } from "views_shared/app/javascript/utils/urls";
import SavedGroupsBookmark from "../../source_directory/SavedGroupsBookmark";
import { SourceBadge } from "../../source_directory/SourceBadges";
import HighlightAttribute, { highlightValue } from "../../source_directory/HighlightAttribute";
import RequestSampleButton from "../RequestSampleButton";
import launchesLabel from "./launchesLabel";
import productListActionColumnWidth from "./listActionColumn";
import ProductImagePlaceholder, { isPlaceholderProductImage } from "../reusable/product_image_placeholder";
import BodyTextWithExpand from "../../feeds/BodyTextWithExpand";

function ProductCardList({ hit }) {
  const [imgError, setImgError] = useState(false);
  const productPath = productUrl({ slug: hit.slug, migrate_id: hit.migrate_id });
  const company = hit.company?.name;
  const category = hit.product_categories?.[0]?.name;
  const hasMeta = Boolean(company || category);
  const hasRealImage = !isPlaceholderProductImage(hit.profile_image_url) && !imgError;
  const priceLine = [hit.price > 0 ? `$${hit.price.toLocaleString()} USD` : null, launchesLabel(hit.released_at)]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className="sd-list-row"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "12px 16px",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
      }}
    >
      {/* Thumbnail — same white-frame-with-hairline treatment as the grid card, scaled down; a
          product with no image of its own gets the gray rounded-tile placeholder. */}
      <div
        style={{
          width: 56,
          height: 56,
          background: "#FFFFFF",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          padding: 6,
          boxSizing: "border-box",
        }}
      >
        {hasRealImage ? (
          <img
            src={hit.profile_image_url}
            alt={hit.name}
            onError={() => setImgError(true)}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          />
        ) : (
          <ProductImagePlaceholder iconFontSize="1.1rem" />
        )}
      </div>

      {/* Product: meta, title, price/sample row, then description underneath — one wide
          column under the single "Product" header (matches the Figma list layout). */}
      <div className="sd-list-row__col" style={{ flex: 1, minWidth: 0 }}>
        {hasMeta && (
          <div style={{ fontSize: 11, color: "#6b7280" }}>
            {company && <HighlightAttribute hit={hit} attribute={["company", "name"]} fallback={company} />}
            {company && category ? " · " : ""}
            {category}
          </div>
        )}
        <div style={{ fontWeight: 700, fontSize: 14 }}>
          <a href={productPath} style={{ color: "#111827" }}>
            <HighlightAttribute hit={hit} attribute="name" fallback={hit.name} />
          </a>
        </div>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 2 }}>
          {priceLine && <span style={{ fontSize: 12, color: "#374151" }}>{priceLine}</span>}
          {hit.samples_available && <SourceBadge type="sample_available" size="sm" />}
          <RequestSampleButton
            productId={hit.id}
            productName={hit.name}
            samplesAvailable={hit.samples_available}
            variant="link"
          />
        </div>
        {hit.description && (
          <BodyTextWithExpand
            body={hit.description}
            maxLength={120}
            highlightValue={highlightValue(hit, "description")}
            textClassName=""
            textStyle={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}
            toggleClassName="paragraph-xs"
          />
        )}
      </div>

      {/* Actions */}
      <div
        className="sd-list-row__col"
        style={{
          flex: `0 0 ${productListActionColumnWidth()}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 12,
        }}
      >
        <a
          href={productPath}
          className="btn btn-primary btn-sm"
          style={{ background: "#4f46e5", border: "none", borderRadius: 8 }}
        >
          View Product
        </a>
        <SavedGroupsBookmark
          sourceableType="Id::PromoEntity::Product"
          sourceableId={hit.id}
          sourceMeta={{ name: hit.name, imageUrl: hit.profile_image_url, slug: hit.slug }}
        />
      </div>
    </div>
  );
}

ProductCardList.propTypes = {
  hit: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    slug: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    profile_image_url: PropTypes.string,
    price: PropTypes.number,
    released_at: PropTypes.string,
    samples_available: PropTypes.bool,
    description: PropTypes.string,
    company: PropTypes.shape({ name: PropTypes.string }),
    product_categories: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
  }).isRequired,
};

export default ProductCardList;
